import { z } from 'zod';
import { addDays, type LocalDate } from '../local-date.js';
import { VESSEL_CERTIFICATE_CATEGORIES } from './vessel-certificates.js';

/**
 * NR496 ve PMS raporlarının kataloğu (plan M7 Task 28; ADR 0029 K23, K27, K33, K37). Gemi
 * sunucusu ve bulut aynı rapor verisini üretir (`@felix/db` `pmsReport`); ekran, PDF, Excel ve
 * CSV aynı tabloyu kullanır. Raporlar yalnız görüntülenir ve dışa aktarılır [NR496 4.6]: `reports`
 * modülünde yalnız `view` ve `export` verilebilir (`NON_GRANTABLE`).
 */

export const PMS_REPORT_GROUPS = ['daily', 'weekly', 'monthly', 'class', 'nr496'] as const;
export type PmsReportGroup = (typeof PMS_REPORT_GROUPS)[number];

/**
 * Raporun kaynağı (başlıkta yazar): `records` değiştirilemez tablolar, `audit_log` ve onaylı ya da
 * raporlanmış (kilitli) kayıtlar [NR496 4.6]; `master_data` ana verinin basım anındaki hâli;
 * `mixed` ikisi (ör. plan: kayıtlar ve iş tanımları).
 */
export type PmsReportBasis = 'records' | 'master_data' | 'mixed';

/**
 * Dönem: `day` tek gemi günü; `range` gün aralığı; `year` takvim yılı; `none` basım anı (süzgeç
 * yok).
 */
export type PmsReportPeriod = 'day' | 'range' | 'year' | 'none';

/**
 * Hücre türü: `code` değeri bir koddur (durum, sonuç, tür), metni dilden gelir; `datetime`
 * ISO-8601 UTC an; `hours` çalışma saati; `percent` yüzde.
 */
export type PmsReportCellKind =
  | 'text'
  | 'code'
  | 'date'
  | 'datetime'
  | 'number'
  | 'hours'
  | 'percent'
  | 'boolean';

export type PmsReportCell = string | number | boolean | null;

export interface PmsReportColumn {
  key: string;
  kind: PmsReportCellKind;
}

export interface PmsReportTableDef {
  key: string;
  columns: readonly PmsReportColumn[];
}

/**
 * Rapora özgü süzgeçler (BV uyum kontrolü §6 eksik 2, 4): klas ve kritik [NR496 4.4.2b, 4.5.3e,
 * 4.6.3c]; History'de ayrıca planlı ya da plansız, gecikmiş ve ertelenmiş raporlanan [NR496
 * 4.6.2b]. Değerler dizgidir (adres ve IPC'de aynı kalır).
 */
export const PMS_REPORT_FILTER_KEYS = [
  'class',
  'critical',
  'planning',
  'overdue',
  'postponed',
] as const;
export type PmsReportFilterKey = (typeof PMS_REPORT_FILTER_KEYS)[number];

export const PMS_REPORT_FILTER_VALUES: Readonly<Record<PmsReportFilterKey, readonly string[]>> = {
  class: ['yes', 'no'],
  critical: ['yes', 'no'],
  planning: ['planned', 'unplanned'],
  overdue: ['yes', 'no'],
  postponed: ['yes', 'no'],
};

/** Raporun uygulanan süzgeçleri (başlıkta ve hash'te). */
export type PmsReportFilters = Partial<Record<PmsReportFilterKey, string>>;

export interface PmsReportDef {
  id: PmsReportId;
  group: PmsReportGroup;
  basis: PmsReportBasis;
  period: PmsReportPeriod;
  /** Okunan tablolar (rapor başlığında "Source"). */
  sources: readonly string[];
  /** Mevzuat ya da karar dayanağı (başlıkta). */
  rule: string;
  tables: readonly PmsReportTableDef[];
  /** Rapora özgü süzgeçler; yoksa yalnız dönem ve ekipman. */
  filters?: readonly PmsReportFilterKey[];
}

const c = (key: string, kind: PmsReportCellKind = 'text'): PmsReportColumn => ({ key, kind });

/** İş kaydı sütunları (iş geçmişi, günlük rapor, geç girişler). */
const RECORD_HEAD = [
  c('woNo'),
  c('jobCode'),
  c('jobTitle'),
  c('equipmentCode'),
  c('equipmentName'),
] as const;

const PLAN_COLUMNS = [
  c('jobCode'),
  c('jobTitle'),
  c('equipmentCode'),
  c('equipmentName'),
  c('critical', 'boolean'),
  c('classItem', 'boolean'),
  c('intervalEvery', 'number'),
  c('intervalUnit', 'code'),
  c('intervalHours', 'hours'),
  c('lastDoneOn', 'date'),
  c('lastDoneHours', 'hours'),
  c('dueOn', 'date'),
  c('dueHours', 'hours'),
  c('remainingDays', 'number'),
  c('remainingHours', 'hours'),
  c('status', 'code'),
  c('pendingApproval', 'boolean'),
  c('postponementPending', 'boolean'),
] as const;

const DEFECT_COLUMNS = [
  c('defectNo'),
  c('equipmentCode'),
  c('equipmentName'),
  c('reportedOn', 'date'),
  c('description'),
  c('severity', 'code'),
  c('operationalImpact', 'boolean'),
  c('status', 'code'),
  c('closure', 'code'),
  c('conditionAfter', 'code'),
  c('repairWoNo'),
  c('classNotification'),
  c('reapprovalPending', 'boolean'),
] as const;

const CHANGE_COLUMNS = [
  c('at', 'datetime'),
  c('shipTime'),
  c('code'),
  c('name'),
  c('op', 'code'),
  c('field', 'code'),
  c('oldValue'),
  c('newValue'),
  c('user'),
  c('device'),
  c('matrixVersion', 'number'),
] as const;

const EQUIPMENT_COLUMNS = [
  c('code'),
  c('name'),
  c('nodeType', 'code'),
  c('parentCode'),
  c('sfiCode'),
  c('maker'),
  c('model'),
  c('serialNo'),
  c('location'),
  c('status', 'code'),
  c('critical', 'boolean'),
  c('criticalSource'),
  c('classRelevant', 'boolean'),
  c('bvItemCode'),
  c('surveyPrinciple', 'code'),
  c('hasCounter', 'boolean'),
  c('outOfPms', 'boolean'),
] as const;

const JOB_COLUMNS = [
  c('jobCode'),
  c('jobTitle'),
  c('equipmentCode'),
  c('equipmentName'),
  c('workType'),
  c('jobType', 'code'),
  c('priority', 'code'),
  c('intervalEvery', 'number'),
  c('intervalUnit', 'code'),
  c('intervalHours', 'hours'),
  c('conditionPoints', 'number'),
  c('intervalBasis', 'code'),
  c('classItem', 'boolean'),
  c('critical', 'boolean'),
  c('ceSurvey', 'boolean'),
  c('responsible'),
  c('instruction'),
  c('active', 'boolean'),
] as const;

/** Bakım planı dışa aktarımı: iş açıklaması, prosedür adımları ve güvenlik maddeleri [NR496 4.4.2c]. */
const PLAN_EXPORT_COLUMNS = [
  ...JOB_COLUMNS.slice(0, -1),
  c('procedure'),
  c('safetyItems'),
  c('active', 'boolean'),
] as const;

const CLASS_CRITICAL = ['class', 'critical'] as const;

const def = <const I extends string>(value: Omit<PmsReportDef, 'id'> & { id: I }) => value;

/** Katalog sırası: grup sırası, grupta ekrandaki sıra (plan Task 28 tablosu). */
const DEFS = [
  def({
    id: 'daily-maintenance',
    group: 'daily',
    basis: 'mixed',
    period: 'day',
    sources: ['work_records', 'record_voids', 'approvals', 'job_definitions'],
    rule: 'NR496 4.5.3',
    tables: [
      {
        key: 'completed',
        columns: [
          ...RECORD_HEAD,
          c('doneOn', 'date'),
          c('enteredAt', 'datetime'),
          c('result', 'code'),
          c('conditionAfter', 'code'),
          c('performedBy'),
          c('status', 'code'),
        ],
      },
      {
        key: 'pending',
        columns: [
          c('jobCode'),
          c('jobTitle'),
          c('equipmentCode'),
          c('equipmentName'),
          c('critical', 'boolean'),
          c('dueOn', 'date'),
          c('dueHours', 'hours'),
          c('status', 'code'),
        ],
      },
    ],
  }),
  def({
    id: 'pms-status',
    group: 'weekly',
    basis: 'mixed',
    period: 'none',
    sources: ['job_definitions', 'work_records', 'postponements', 'job_interval_changes'],
    rule: 'NR496 4.5.3a, 4.5.3e',
    filters: CLASS_CRITICAL,
    tables: [{ key: 'jobs', columns: PLAN_COLUMNS }],
  }),
  def({
    id: 'overdue',
    group: 'weekly',
    basis: 'mixed',
    period: 'none',
    sources: ['job_definitions', 'work_records', 'postponements', 'approvals'],
    // Gecikmiş işlerin süzülmesi [4.5.3e] (BV uyum kontrolü §3.1)
    rule: 'NR496 4.5.3c, 4.5.3e; K23',
    filters: CLASS_CRITICAL,
    tables: [
      {
        key: 'jobs',
        columns: [
          c('jobCode'),
          c('jobTitle'),
          c('equipmentCode'),
          c('equipmentName'),
          c('critical', 'boolean'),
          c('dueOn', 'date'),
          c('dueHours', 'hours'),
          c('currentHours', 'hours'),
          c('overdueDays', 'number'),
          c('overdueHours', 'hours'),
          c('lastDoneOn', 'date'),
          c('pendingApproval', 'boolean'),
          c('postponementPending', 'boolean'),
        ],
      },
    ],
  }),
  def({
    id: 'critical-equipment',
    group: 'weekly',
    basis: 'mixed',
    period: 'none',
    sources: ['equipment', 'job_definitions', 'work_records', 'defects'],
    rule: 'NR496 4.4; ISM 10.3',
    tables: [
      {
        key: 'equipment',
        columns: [
          c('code'),
          c('name'),
          c('criticalSource'),
          c('criticalReason'),
          c('jobs', 'number'),
          c('overdue', 'number'),
          c('openDefects', 'number'),
          c('outOfPms', 'boolean'),
        ],
      },
      {
        key: 'jobs',
        columns: [
          c('equipmentCode'),
          c('jobCode'),
          c('jobTitle'),
          c('dueOn', 'date'),
          c('dueHours', 'hours'),
          c('status', 'code'),
        ],
      },
    ],
  }),
  def({
    id: 'pms-compliance',
    group: 'monthly',
    basis: 'records',
    period: 'range',
    sources: ['work_records', 'record_voids', 'approvals', 'postponements', 'job_definitions'],
    rule: 'BIMCO PI065; K23',
    tables: [
      {
        key: 'jobs',
        columns: [
          c('jobCode'),
          c('jobTitle'),
          c('equipmentCode'),
          c('critical', 'boolean'),
          c('dueOn', 'date'),
          c('doneOn', 'date'),
          c('status', 'code'),
        ],
      },
    ],
  }),
  def({
    id: 'breakdowns',
    group: 'monthly',
    basis: 'records',
    period: 'range',
    sources: ['defects', 'submissions', 'approvals'],
    rule: 'NR496 4.5.4; UR Z20 3.2.5',
    tables: [{ key: 'defects', columns: DEFECT_COLUMNS }],
  }),
  def({
    id: 'defects',
    group: 'monthly',
    basis: 'records',
    period: 'range',
    sources: ['defects', 'submissions', 'approvals'],
    rule: 'NR496 4.3.2d; ISM 10.2',
    tables: [{ key: 'defects', columns: DEFECT_COLUMNS }],
  }),
  def({
    id: 'spare-parts',
    group: 'monthly',
    basis: 'records',
    period: 'range',
    sources: ['stock_movements', 'work_records'],
    rule: 'K17',
    tables: [
      {
        key: 'movements',
        columns: [
          c('date', 'date'),
          c('material'),
          c('kind', 'code'),
          c('quantity', 'number'),
          c('unit'),
          c('woNo'),
          c('jobCode'),
          c('equipmentCode'),
          c('reason'),
        ],
      },
    ],
  }),
  def({
    id: 'class-due-list',
    group: 'class',
    basis: 'mixed',
    period: 'none',
    sources: ['class_item_list', 'work_records', 'class_confirmations'],
    rule: 'NR467 Pt A Ch 2 App 1 2.4; UR Z20',
    tables: [
      {
        key: 'items',
        columns: [
          c('itemCode'),
          c('description'),
          c('category', 'code'),
          c('surveyPrinciple', 'code'),
          c('intervalMonths', 'number'),
          c('equipment'),
          c('lastSurveyOn', 'date'),
          c('ceCredited', 'boolean'),
          c('rejection', 'code'),
          c('lastConfirmedOn', 'date'),
          c('surveyor'),
          c('dueOn', 'date'),
          c('state', 'code'),
        ],
      },
    ],
  }),
  def({
    id: 'survey-status',
    group: 'class',
    basis: 'master_data',
    period: 'none',
    sources: ['vessel_surveys'],
    rule: 'SOLAS I/10; UR Z20',
    tables: [
      {
        key: 'surveys',
        columns: [
          c('kind', 'code'),
          c('lastDoneOn', 'date'),
          c('nextDueOn', 'date'),
          c('windowFrom', 'date'),
          c('windowTo', 'date'),
          c('state', 'code'),
          c('overrideReason'),
          c('remarks'),
        ],
      },
    ],
  }),
  def({
    id: 'vessel-certificates',
    group: 'class',
    basis: 'master_data',
    period: 'none',
    sources: [
      'vessel_certificates',
      'certificate_endorsements',
      'certificate_issuances_on_board',
      'vessel_certificate_types',
    ],
    rule: 'ISM 13.4; SOLAS I/10; K28, K30',
    tables: [
      {
        key: 'certificates',
        columns: [
          c('category', 'code'),
          c('typeCode'),
          c('name'),
          c('number'),
          c('issuedBy'),
          c('issueDate', 'date'),
          c('expiryDate', 'date'),
          c('daysLeft', 'number'),
          c('status', 'code'),
          c('nextSurveyKind', 'code'),
          c('nextSurveyDue', 'date'),
          c('windowFrom', 'date'),
          c('windowTo', 'date'),
          c('state', 'code'),
          c('source', 'code'),
          c('officeEntryPending', 'boolean'),
          c('pages', 'number'),
          c('superseded', 'boolean'),
        ],
      },
    ],
  }),
  def({
    id: 'conditions-of-class',
    group: 'class',
    basis: 'master_data',
    period: 'none',
    sources: ['class_findings'],
    rule: 'NR467 Pt A Ch 2 App 1 5.3.3; UR Z20 3.3.3',
    tables: [
      {
        key: 'findings',
        columns: [
          c('kind', 'code'),
          c('number'),
          c('text'),
          c('imposedOn', 'date'),
          c('dueOn', 'date'),
          c('closedOn', 'date'),
          c('equipmentCode'),
          c('state', 'code'),
        ],
      },
    ],
  }),
  def({
    id: 'annual-pms',
    group: 'class',
    basis: 'mixed',
    period: 'year',
    sources: ['equipment', 'job_definitions', 'job_interval_changes', 'audit_log'],
    rule: 'UR Z20 2.3.3; K33',
    tables: [
      { key: 'equipment', columns: EQUIPMENT_COLUMNS },
      { key: 'jobs', columns: JOB_COLUMNS },
      {
        key: 'changes',
        columns: [c('table', 'code'), ...CHANGE_COLUMNS],
      },
    ],
  }),
  def({
    id: 'running-hours',
    group: 'nr496',
    basis: 'records',
    period: 'range',
    sources: ['running_hour_readings', 'record_voids'],
    rule: 'NR496 4.5.1',
    tables: [
      {
        key: 'readings',
        columns: [
          c('equipmentCode'),
          c('equipmentName'),
          c('readOn', 'date'),
          c('kind', 'code'),
          c('meterHours', 'hours'),
          c('cumulativeHours', 'hours'),
          c('previousMeterFinal', 'hours'),
          c('meterStartHours', 'hours'),
          c('source', 'code'),
          c('confirmations'),
          c('reason'),
          c('enteredAt', 'datetime'),
          c('enteredBy'),
          c('voidReason'),
        ],
      },
    ],
  }),
  def({
    id: 'postponements',
    group: 'nr496',
    basis: 'records',
    period: 'range',
    sources: ['postponements', 'submissions', 'approvals', 'office_decisions'],
    rule: 'NR496 4.5.3d',
    tables: [
      {
        key: 'postponements',
        columns: [
          c('jobCode'),
          c('jobTitle'),
          c('equipmentCode'),
          c('woNo'),
          c('cycleBase', 'code'),
          c('firstOriginalDueOn', 'date'),
          c('firstOriginalDueHours', 'hours'),
          c('previousDueOn', 'date'),
          c('previousDueHours', 'hours'),
          c('newDueOn', 'date'),
          c('newDueHours', 'hours'),
          c('reason'),
          c('classReference'),
          c('requestedAt', 'datetime'),
          c('requestedBy'),
          c('status', 'code'),
          c('decisions'),
        ],
      },
    ],
  }),
  def({
    id: 'maintenance-history',
    group: 'nr496',
    basis: 'records',
    period: 'range',
    sources: [
      'work_records',
      'record_voids',
      'submissions',
      'approvals',
      'job_definitions',
      'job_interval_changes',
      'postponements',
    ],
    // History raporu [4.6.2] (BV uyum kontrolü §3.1); değiştirilemezlik ve silinen tanımlar
    rule: 'NR496 4.6.2; 4.5.3f; 4.3.2a',
    filters: ['class', 'critical', 'planning', 'overdue', 'postponed'],
    tables: [
      {
        key: 'records',
        // 4.6.2a: ekipman, kategori (planlı/plansız), iş adı ve kodu, iş türü, aralık, vade,
        // tamamlanma günü ya da saati, gecikme ve erteleme bilgisi, açıklamalar
        columns: [
          ...RECORD_HEAD,
          c('category', 'code'),
          c('kind', 'code'),
          c('jobType', 'code'),
          c('intervalEvery', 'number'),
          c('intervalUnit', 'code'),
          c('intervalHours', 'hours'),
          c('dueOn', 'date'),
          c('dueHours', 'hours'),
          c('doneOn', 'date'),
          c('enteredAt', 'datetime'),
          c('runningHours', 'hours'),
          c('overdue', 'boolean'),
          c('postponedOn', 'date'),
          c('postponementReason'),
          c('result', 'code'),
          c('conditionAfter', 'code'),
          // Gecikme gerekçesi [NR496 4.6.2a, 4.5.4b] (ADR 0029 Ek M7f F1)
          c('overdueReason'),
          c('performedBy'),
          c('workPerformed'),
          c('remarks'),
          c('status', 'code'),
          c('voidReason'),
          c('contentHash'),
        ],
      },
    ],
  }),
  def({
    id: 'maintenance-plan',
    group: 'nr496',
    basis: 'master_data',
    period: 'none',
    sources: ['job_definitions', 'job_interval_changes', 'equipment'],
    rule: 'NR496 4.4; 4.4.2b, 4.4.2c',
    filters: CLASS_CRITICAL,
    tables: [{ key: 'jobs', columns: PLAN_EXPORT_COLUMNS }],
  }),
  def({
    id: 'equipment-list',
    group: 'nr496',
    basis: 'master_data',
    period: 'none',
    sources: ['equipment'],
    rule: 'NR496 4.4',
    filters: CLASS_CRITICAL,
    tables: [{ key: 'equipment', columns: EQUIPMENT_COLUMNS }],
  }),
  def({
    id: 'equipment-changes',
    group: 'nr496',
    basis: 'records',
    period: 'range',
    // Çalışma saati kayıtları ve iptalleri de ekipmanın izidir [4.6.3a]
    sources: ['audit_log', 'running_hour_readings', 'record_voids'],
    rule: 'NR496 4.6.3a, 4.6.3c, 4.6.3e',
    filters: CLASS_CRITICAL,
    tables: [{ key: 'changes', columns: [...CHANGE_COLUMNS, c('reason')] }],
  }),
  def({
    id: 'job-changes',
    group: 'nr496',
    basis: 'records',
    period: 'range',
    rule: 'NR496 4.6.3b, 4.6.3c, 4.6.3e',
    // İşin kritikliği ekipmandan türetilir: ekipmanın kritiklik değişikliği işte görünür [4.6.3b]
    sources: ['audit_log'],
    filters: CLASS_CRITICAL,
    tables: [{ key: 'changes', columns: [...CHANGE_COLUMNS, c('derivedFrom')] }],
  }),
  def({
    id: 'interval-changes',
    group: 'nr496',
    basis: 'records',
    period: 'range',
    sources: ['job_interval_changes', 'submissions', 'approvals', 'office_decisions'],
    // Aralık değişikliği işin izlenebilirliğidir [4.6.3b] (BV uyum kontrolü §3.1)
    rule: 'K11; UR Z20 2.3.5; NR496 4.6.3b',
    tables: [
      {
        key: 'changes',
        columns: [
          c('jobCode'),
          c('jobTitle'),
          c('equipmentCode'),
          c('previousEvery', 'number'),
          c('previousUnit', 'code'),
          c('previousHours', 'hours'),
          c('newEvery', 'number'),
          c('newUnit', 'code'),
          c('newHours', 'hours'),
          c('direction', 'code'),
          c('intervalBasis', 'code'),
          c('reason'),
          c('classReference'),
          c('requestedAt', 'datetime'),
          c('requestedBy'),
          c('status', 'code'),
          c('classItemMonths', 'number'),
          c('exceedsClassItem', 'boolean'),
        ],
      },
    ],
  }),
  def({
    id: 'cm-data',
    group: 'nr496',
    basis: 'records',
    period: 'range',
    sources: ['cm_import_batches', 'measurements', 'audit_log'],
    rule: 'UR Z27 2.2.4; NR496 4.4.2d',
    tables: [
      {
        key: 'imports',
        columns: [
          c('importedAt', 'datetime'),
          c('fileName'),
          c('sha256'),
          c('provider'),
          c('mode', 'code'),
          c('rowsTotal', 'number'),
          c('pointsCreated', 'number'),
          c('pointsUpdated', 'number'),
          c('measurementsAdded', 'number'),
          c('defectsOpened', 'number'),
          c('voidReason'),
        ],
      },
      { key: 'limitChanges', columns: CHANGE_COLUMNS },
      {
        key: 'measurements',
        columns: [
          c('measuredAt', 'datetime'),
          c('equipmentCode'),
          c('point'),
          c('value', 'number'),
          c('unit'),
          c('result', 'code'),
          c('source', 'code'),
          c('woNo'),
          c('voidReason'),
        ],
      },
    ],
  }),
  def({
    id: 'late-entries',
    group: 'nr496',
    basis: 'records',
    period: 'range',
    sources: ['work_records', 'record_voids'],
    rule: 'K27; MEPC.312(74) 4.3.7',
    tables: [
      {
        key: 'records',
        columns: [
          ...RECORD_HEAD,
          c('doneOn', 'date'),
          c('enteredAt', 'datetime'),
          c('delayDays', 'number'),
          c('enteredBy'),
          c('status', 'code'),
        ],
      },
    ],
  }),
] as const;

export const PMS_REPORT_IDS = DEFS.map((d) => d.id) as unknown as readonly [
  (typeof DEFS)[number]['id'],
  ...(typeof DEFS)[number]['id'][],
];
export type PmsReportId = (typeof DEFS)[number]['id'];

export const PMS_REPORTS: readonly PmsReportDef[] = DEFS as unknown as readonly PmsReportDef[];

export function pmsReportDef(id: PmsReportId): PmsReportDef {
  return PMS_REPORTS.find((d) => d.id === id) as PmsReportDef;
}

/** Raporun süzgeçlerinden yalnız rapora uyanlar (başlıkta ve hash'te). */
export function pmsReportFilters(
  def: Pick<PmsReportDef, 'filters'>,
  query: PmsReportQuery,
): PmsReportFilters {
  const result: PmsReportFilters = {};
  for (const key of def.filters ?? []) {
    const value = query[key];
    if (value !== undefined) result[key] = value;
  }
  return result;
}

export function isPmsReportId(value: unknown): value is PmsReportId {
  return typeof value === 'string' && PMS_REPORTS.some((d) => d.id === value);
}

/** Dışa aktarma biçimleri (plan Task 29): PDF istemcide üretilir; Excel ve CSV sunucuda. */
export const PMS_REPORT_FORMATS = ['xlsx', 'csv'] as const;
export type PmsReportFormat = (typeof PMS_REPORT_FORMATS)[number];

export const PMS_REPORT_LANGUAGES = ['en', 'tr'] as const;
export type PmsReportLanguage = (typeof PMS_REPORT_LANGUAGES)[number];

/**
 * Süzgeç: gün aralığı (gemi yerel günü), yıl ve ekipman (alt ağacıyla; Equipment List "Export").
 * Basım anındaki raporlarda gün aralığı yok sayılır.
 */
export const pmsReportQuerySchema = z
  .strictObject({
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    equipment: z.uuid().optional(),
    /** Vessel Certificates: kategori (ağaç düğümü; Task 27a Export). */
    category: z.enum(VESSEL_CERTIFICATE_CATEGORIES).optional(),
    // Rapora özgü süzgeçler (`PmsReportDef.filters`); rapora uymayan yok sayılır
    class: z.enum(['yes', 'no']).optional(),
    critical: z.enum(['yes', 'no']).optional(),
    planning: z.enum(['planned', 'unplanned']).optional(),
    overdue: z.enum(['yes', 'no']).optional(),
    postponed: z.enum(['yes', 'no']).optional(),
  })
  .refine((query) => !query.from || !query.to || query.from <= query.to, { path: ['to'] });
export type PmsReportQuery = z.output<typeof pmsReportQuerySchema>;

/** Varsayılan aralık gün sayısı: haftalık 7, diğerleri 30 (K23 "dönem varsayılan son 30 gün"). */
const DEFAULT_DAYS: Readonly<Record<PmsReportGroup, number>> = {
  daily: 1,
  weekly: 7,
  monthly: 30,
  class: 30,
  nr496: 30,
};

/** En uzun aralık: 5 yıllık geçmiş tek raporda [NR496 4.3.1]; daha uzunu `RANGE_TOO_LONG`. */
export const PMS_REPORT_MAX_DAYS = 5 * 366 + 1;

export interface PmsReportRange {
  from: LocalDate | null;
  to: LocalDate | null;
}

/**
 * Raporun dönemi: tek gün `to` (varsayılan bugün); aralıkta verilmeyen uç bugün ve grubun
 * varsayılan gün sayısı; yılda 1 Ocak – 31 Aralık (varsayılan bu yıl); basım anında yok.
 */
export function pmsReportRange(
  def: Pick<PmsReportDef, 'group' | 'period'>,
  query: PmsReportQuery,
  today: LocalDate,
): PmsReportRange {
  switch (def.period) {
    case 'none':
      return { from: null, to: null };
    case 'day': {
      const day = query.to ?? query.from ?? today;
      return { from: day, to: day };
    }
    case 'year': {
      const year = query.year ?? Number(today.slice(0, 4));
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    }
    case 'range': {
      const to = query.to ?? today;
      const from = query.from ?? addDays(to, -(DEFAULT_DAYS[def.group] - 1));
      return { from, to };
    }
  }
}

/**
 * Dışa aktarılan dosyanın adı [NR496 4.1.2]: ürün, sürüm, rapor ve basım günü
 * (`felix-manager-<sürüm>-<rapor>-<YYYYMMDD>.<uzantı>`). Sürümdeki izin verilmeyen karakterler
 * tireye döner (dosya adı ASCII kalır).
 */
export function pmsReportFileName(
  version: string,
  id: PmsReportId,
  day: LocalDate,
  extension: PmsReportFormat | 'pdf',
): string {
  const safe = version.replace(/[^0-9A-Za-z.]+/g, '-').replace(/^-+|-+$/g, '') || '0';
  return `felix-manager-${safe}-${id}-${day.replaceAll('-', '')}.${extension}`;
}

export const PRODUCT_NAME = 'Felixmate';

/** Geç giriş eşiği (K27): yapılıştan bu kadar gün sonra girilen kayıt raporda listelenir. */
export const LATE_ENTRY_DAYS = 7;
